/**
 * What a business can be asked, given the records it actually keeps — and what
 * its home screen shows before anybody customizes anything.
 *
 * The sibling of `insight-candidates.ts`, and bounded the same way: tiers, each
 * one capped, the whole list capped, so opening the gallery is a known number
 * of queries rather than a cross product.
 *
 * **"It should depend on the templates the business has" is answered here**,
 * and it costs nothing: `record_catalog` already carries every record's template,
 * direction, currency and kind. So a shop keeping only a stock register is
 * never offered a rupee total, and a shop keeping both a sales record and an
 * expenses record is offered profit first. The gates read direction and currency
 * rather than matching template id strings, so a template applied twice, or a
 * record somebody built by hand, is handled without a special case.
 */
import type { RecordListItem } from '@alluzo/shared';

import type { InsightCardId } from '@/lib/insight-cards';
import {
  businessEntry,
  type BusinessInsightLayoutEntry,
} from '@/lib/insight-business-layout';
import type { BusinessInsightSpec } from '@/lib/insight-business-query';
import { pickTodayCurrency } from '@/lib/today-totals';

/* ---------------------------------------------------------- capabilities */

/** One record, for the pickers that name records. */
export type BusinessRecordRef = {
  readonly id: string;
  readonly name: string;
  readonly templateId: string | null;
};

/**
 * What this business can be asked about, read off its catalog.
 *
 * `hasIn` and `hasOut` are computed over the MONEY records in the picked currency
 * only — the same set the SQL will count. A record in another currency cannot
 * make a rupee business look profit-capable, and a stock register (which
 * carries no currency at all) cannot either.
 *
 * A record whose sign is a field counts as both directions, because its rows
 * genuinely go both ways. That is why a business keeping only Party Dues is
 * offered a net figure: net balance movement is exactly what a dues record is for.
 */
export type BusinessCapabilities = {
  /** The one currency every money card is in, or null when there is none. */
  readonly currency: string | null;
  readonly hasMoney: boolean;
  readonly hasIn: boolean;
  readonly hasOut: boolean;
  /** Both directions exist, so "net" means something no single record can say. */
  readonly profitCapable: boolean;
  readonly moneyRecordCount: number;
  readonly recordCount: number;
  readonly listCount: number;
  readonly templateIds: readonly string[];
  /** Every record, in catalog order. What the condition sheet lists. */
  readonly records: readonly BusinessRecordRef[];
};

export const capabilitiesOf = (catalog: readonly RecordListItem[]): BusinessCapabilities => {
  const currency = pickTodayCurrency(catalog);
  const records = catalog.filter((row) => row.kind === 'record');
  const money = records.filter((row) => row.direction !== null && row.currency === currency && currency !== null);
  const hasIn = money.some((row) => row.direction === 'in' || row.direction === 'mixed');
  const hasOut = money.some((row) => row.direction === 'out' || row.direction === 'mixed');

  return {
    currency,
    hasMoney: money.length > 0,
    hasIn,
    hasOut,
    profitCapable: hasIn && hasOut,
    moneyRecordCount: money.length,
    recordCount: records.length,
    listCount: catalog.length - records.length,
    templateIds: [
      ...new Set(records.map((row) => row.templateId).filter((id): id is string => id !== null)),
    ],
    records: records.map((row) => ({ id: row.id, name: row.name, templateId: row.templateId })),
  };
};

/* --------------------------------------------------------- the default strip */

const IN_TODAY: BusinessInsightSpec = { shape: 'scalar', measure: { agg: 'sum', flow: 'in' }, scope: 'day' };
const OUT_TODAY: BusinessInsightSpec = { shape: 'scalar', measure: { agg: 'sum', flow: 'out' }, scope: 'day' };
const NET_MONTH_PAIR: BusinessInsightSpec = {
  shape: 'scalar_pair',
  measure: { agg: 'sum', flow: 'net' },
  scope: 'month',
};
const ENTRIES_TODAY: BusinessInsightSpec = { shape: 'scalar', measure: { agg: 'rows' }, scope: 'day' };

/**
 * What home shows before anybody edits it.
 *
 * The first two entries reproduce the strip that is already there, down to the
 * catalogue keys: this screen has always shown money in and money out for
 * today, and making that the DEFAULT of an editable strip is what lets the
 * whole feature ship without changing what anybody sees. The parity test in
 * `insight-business-query.sqlite.test.ts` pins the numbers to `toTodayTotals`
 * so the two cannot drift.
 *
 * A business that can say "net" gets it as well, because that is the number no
 * single record can answer and the reason a business-wide strip exists at all.
 * A business with no money at all gets a count, so a stock register still leads
 * with something true.
 */
export const defaultBusinessLayoutFor = (
  capabilities: BusinessCapabilities,
): BusinessInsightLayoutEntry[] => {
  if (capabilities.recordCount === 0) return [];
  if (!capabilities.hasMoney) {
    return [businessEntry('insight.stat', ENTRIES_TODAY, 'half', 'insight.biz.entries_today')];
  }
  const strip = [
    businessEntry('insight.stat', IN_TODAY, 'half', 'home.today_in'),
    businessEntry('insight.stat', OUT_TODAY, 'half', 'home.today_out'),
  ];
  if (!capabilities.profitCapable) return strip;
  return [...strip, businessEntry('insight.delta', NET_MONTH_PAIR, 'full', 'insight.biz.profit_month')];
};

/* ------------------------------------------------------------- the gallery */

/**
 * How many cards the business gallery will ever offer.
 *
 * Larger than the record gallery's fourteen, and deliberately: a business has
 * four tiers of question rather than three, and at fourteen a two-record money
 * business could not reach the activity tier even taking the tiers round by
 * round. Three of these statements scan every live row (the all-time ones);
 * the rest are day ranges on the index migration 3 adds. They arrive in
 * batches between frames, as the record gallery's do.
 */
export const BUSINESS_GALLERY_BUDGET = 18;

export type BusinessCandidateGroup =
  | 'insight.biz.group.suggested'
  | 'insight.biz.group.money'
  | 'insight.biz.group.time'
  | 'insight.biz.group.records'
  | 'insight.biz.group.activity';

export type BusinessCandidate = {
  readonly entry: BusinessInsightLayoutEntry;
  readonly group: BusinessCandidateGroup;
  /** True when this exact card is already on the strip. */
  readonly placed: boolean;
};

type Draft = {
  readonly card: InsightCardId;
  readonly spec: BusinessInsightSpec;
  readonly group: BusinessCandidateGroup;
  readonly labelKey?: string;
};

const MEASURE_NET = { agg: 'sum', flow: 'net' } as const;
const MEASURE_IN = { agg: 'sum', flow: 'in' } as const;
const MEASURE_OUT = { agg: 'sum', flow: 'out' } as const;
const ROWS = { agg: 'rows' } as const;

/** Tier 1: the money a business can be asked for, at the windows people ask. */
const moneyDrafts = (capabilities: BusinessCapabilities): Draft[] => {
  const drafts: Draft[] = [];
  if (capabilities.profitCapable) {
    drafts.push(
      { card: 'insight.stat', spec: { shape: 'scalar', measure: MEASURE_NET, scope: 'month' }, group: 'insight.biz.group.money' },
      { card: 'insight.delta', spec: NET_MONTH_PAIR, group: 'insight.biz.group.money', labelKey: 'insight.biz.profit_month' },
      { card: 'insight.stat', spec: { shape: 'scalar', measure: MEASURE_NET, scope: 'all' }, group: 'insight.biz.group.money' },
    );
  }
  if (capabilities.hasIn) {
    drafts.push(
      { card: 'insight.stat', spec: { shape: 'scalar', measure: MEASURE_IN, scope: 'month' }, group: 'insight.biz.group.money' },
      { card: 'insight.delta', spec: { shape: 'scalar_pair', measure: MEASURE_IN, scope: 'month' }, group: 'insight.biz.group.money' },
    );
  }
  if (capabilities.hasOut) {
    drafts.push(
      { card: 'insight.stat', spec: { shape: 'scalar', measure: MEASURE_OUT, scope: 'month' }, group: 'insight.biz.group.money' },
      { card: 'insight.stat', spec: { shape: 'scalar', measure: { agg: 'max', flow: 'out' }, scope: 'month' }, group: 'insight.biz.group.money' },
    );
  }
  return drafts;
};

/** Tier 2: the same money over time, and how busy the business has been. */
const timeDrafts = (capabilities: BusinessCapabilities): Draft[] => {
  const drafts: Draft[] = [];
  if (capabilities.profitCapable) {
    drafts.push(
      { card: 'insight.trend', spec: { shape: 'series', measure: MEASURE_NET, days: 14 }, group: 'insight.biz.group.time' },
      { card: 'insight.bars', spec: { shape: 'series', measure: MEASURE_NET, days: 7 }, group: 'insight.biz.group.time' },
    );
  } else if (capabilities.hasMoney) {
    const flow = capabilities.hasIn ? MEASURE_IN : MEASURE_OUT;
    drafts.push({ card: 'insight.trend', spec: { shape: 'series', measure: flow, days: 14 }, group: 'insight.biz.group.time' });
  }
  drafts.push({ card: 'insight.bars', spec: { shape: 'series', measure: ROWS, days: 7 }, group: 'insight.biz.group.time' });
  return drafts;
};

/**
 * Tier 3: how the records compare with each other.
 *
 * Only worth offering with two records to compare. On one record every one of these
 * is the same number as the record's own card, which the record page already has.
 */
const recordDrafts = (capabilities: BusinessCapabilities): Draft[] => {
  if (capabilities.recordCount < 2) return [];
  const drafts: Draft[] = [];
  if (capabilities.hasMoney) {
    drafts.push(
      { card: 'insight.breakdown', spec: { shape: 'breakdown', measure: MEASURE_NET, by: 'record', scope: 'month', limit: 4 }, group: 'insight.biz.group.records' },
      { card: 'insight.stat', spec: { shape: 'scalar', measure: MEASURE_NET, scope: 'month', leaderOf: 'record' }, group: 'insight.biz.group.records' },
    );
  }
  drafts.push(
    { card: 'insight.breakdown', spec: { shape: 'breakdown', measure: ROWS, by: 'record', scope: 'month', limit: 4 }, group: 'insight.biz.group.records' },
    { card: 'insight.breakdown', spec: { shape: 'breakdown', measure: { agg: 'idle_days' }, by: 'record', scope: 'all', limit: 4 }, group: 'insight.biz.group.records', labelKey: 'insight.biz.quiet_records' },
  );
  return drafts;
};

/**
 * Tier 4: activity, which needs no money at all.
 *
 * The breakdown by writer is offered only when the mirror really holds more
 * than one writer. On a one-person shop it would draw a single row saying
 * "You, 81" — a card that answers a question nobody has.
 */
const activityDrafts = (capabilities: BusinessCapabilities, writers: number): Draft[] => {
  const drafts: Draft[] = [
    { card: 'insight.stat', spec: ENTRIES_TODAY, group: 'insight.biz.group.activity', labelKey: 'insight.biz.entries_today' },
    { card: 'insight.stat', spec: { shape: 'scalar', measure: ROWS, scope: 'month' }, group: 'insight.biz.group.activity' },
    { card: 'insight.stat', spec: { shape: 'scalar', measure: ROWS, scope: 'month', leaderOf: 'day' }, group: 'insight.biz.group.activity', labelKey: 'insight.biz.busiest_day' },
  ];
  if (capabilities.listCount > 0) {
    drafts.push({
      card: 'insight.stat',
      spec: { shape: 'scalar', measure: ROWS, scope: 'all', kind: 'directory' },
      group: 'insight.biz.group.activity',
      labelKey: 'insight.biz.list_rows',
    });
  }
  if (writers >= 2) {
    drafts.push({
      card: 'insight.breakdown',
      spec: { shape: 'breakdown', measure: ROWS, by: 'creator', scope: 'month', limit: 4 },
      group: 'insight.biz.group.activity',
      labelKey: 'insight.biz.by_writer',
    });
    if (capabilities.hasMoney) {
      drafts.push({
        card: 'insight.breakdown',
        spec: { shape: 'breakdown', measure: MEASURE_NET, by: 'record', scope: 'month', limit: 4, filters: [{ key: 'creator', op: 'is', value: 'me' }] },
        group: 'insight.biz.group.activity',
        labelKey: 'insight.biz.my_records',
      });
    }
  }
  return drafts;
};

/**
 * Every candidate for one business, deduped, capped, and flagged with whether
 * it is already on the strip.
 *
 * The tiers are taken a card at a time, round by round, rather than one tier
 * after another. That is not cosmetic: taken in order, a two-record money
 * business fills the budget on money and comparison cards and never reaches
 * the activity tier at all, so a whole section of the gallery would silently
 * not exist. Round by round, every tier is represented and the budget cuts each
 * one's tail instead of a whole group. The display is grouped afterwards, so
 * the sections still read in their own order.
 *
 * Dedupe keeps the FIRST sighting of a question, so a card the default strip
 * already names keeps that name rather than being described a second time.
 *
 * @param writers how many distinct people have written an entry on this device.
 *                One means the by-writer cards are withheld.
 */
export const businessCandidatesFor = (
  capabilities: BusinessCapabilities,
  placed: readonly BusinessInsightLayoutEntry[],
  writers = 1,
): BusinessCandidate[] => {
  // No records, nothing to count. A business mid-onboarding has a catalog of
  // lists and no entries anywhere, and a card over nothing is not an offer.
  if (capabilities.recordCount === 0) return [];

  const onStrip = new Set(placed.map((entry) => entry.id));
  const candidates: BusinessCandidate[] = [];
  const seen = new Set<string>();

  const offer = (entry: BusinessInsightLayoutEntry, group: BusinessCandidateGroup): boolean => {
    if (candidates.length >= BUSINESS_GALLERY_BUDGET) return false;
    if (seen.has(entry.id)) return true;
    seen.add(entry.id);
    candidates.push({ entry, group, placed: onStrip.has(entry.id) });
    return true;
  };

  for (const entry of defaultBusinessLayoutFor(capabilities)) {
    offer(entry, 'insight.biz.group.suggested');
  }

  const tiers: Draft[][] = [
    moneyDrafts(capabilities),
    timeDrafts(capabilities),
    recordDrafts(capabilities),
    activityDrafts(capabilities, writers),
  ];
  const deepest = Math.max(0, ...tiers.map((tier) => tier.length));
  for (let round = 0; round < deepest; round += 1) {
    for (const tier of tiers) {
      const draft = tier[round];
      if (draft === undefined) continue;
      offer(businessEntry(draft.card, draft.spec, undefined, draft.labelKey ?? null), draft.group);
    }
  }

  return candidates;
};

/** The gallery's sections, in order, holding only the groups that have cards. */
export const groupBusinessCandidates = (
  candidates: readonly BusinessCandidate[],
): readonly {
  readonly group: BusinessCandidateGroup;
  readonly candidates: readonly BusinessCandidate[];
}[] => {
  const order: readonly BusinessCandidateGroup[] = [
    'insight.biz.group.suggested',
    'insight.biz.group.money',
    'insight.biz.group.time',
    'insight.biz.group.records',
    'insight.biz.group.activity',
  ];
  return order
    .map((group) => ({ group, candidates: candidates.filter((held) => held.group === group) }))
    .filter((section) => section.candidates.length > 0);
};
